<script setup lang="ts">
import { ref } from 'vue'
import { reportError } from '../lib/api'
import { config } from '../lib/config'
import { auth } from '../lib/auth'

const emit = defineEmits<{ (e: 'close'): void }>()

const message = ref('')
const shot = ref<string | null>(null)
const sending = ref(false)
const sent = ref(false)

function onPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items
  if (!items) return
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) {
        const r = new FileReader()
        r.onload = () => (shot.value = r.result as string)
        r.readAsDataURL(file)
      }
      e.preventDefault()
      break
    }
  }
}

async function send() {
  sending.value = true
  await reportError({
    userMessage: message.value,
    context: 'user-report',
    exceptionType: 'UserReport',
    exceptionMessage: message.value,
    appVersion: config.appVersion,
    platform: 'Web',
    userEmail: auth.user?.email ?? '',
    timestampUtc: new Date().toISOString(),
    screenshotBase64: shot.value ?? '',
  })
  sending.value = false
  sent.value = true
  setTimeout(() => emit('close'), 1200)
}
</script>

<template>
  <div class="overlay" @click.self="emit('close')">
    <div class="dlg">
      <header class="hd">
        <strong>Report a problem</strong>
        <button class="x" @click="emit('close')">×</button>
      </header>
      <div v-if="!sent" class="body">
        <p class="muted">Tell us what happened so we can investigate.</p>
        <textarea v-model="message" rows="4" placeholder="What were you doing when it went wrong?"></textarea>
        <div class="shot" @paste="onPaste" tabindex="0">
          <img v-if="shot" :src="shot" alt="screenshot" />
          <span v-else class="muted">Click here and paste a screenshot (Ctrl+V) — optional</span>
        </div>
        <button v-if="shot" class="clear" @click="shot = null">Remove screenshot</button>
      </div>
      <p v-else class="thanks">Thanks — your report was sent. ✓</p>
      <footer v-if="!sent" class="ft">
        <button @click="emit('close')">Cancel</button>
        <button class="primary" :disabled="sending || !message.trim()" @click="send">
          {{ sending ? 'Sending…' : 'Send report' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.overlay { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.4); display: grid; place-items: center; z-index: 60; padding: 16px; }
.dlg { background: var(--bg); border-radius: 8px; width: 100%; max-width: 360px; display: flex; flex-direction: column; box-shadow: 0 10px 36px rgba(0,0,0,0.35); }
.hd { display: flex; align-items: center; padding: 10px 12px; border-bottom: 1px solid var(--line); }
.hd .x { margin-left: auto; border: none; background: none; font-size: 18px; color: var(--muted); }
.body { padding: 12px; display: flex; flex-direction: column; gap: 8px; }
textarea { width: 100%; padding: 8px; border: 1px solid var(--line); border-radius: 6px; font: inherit; font-size: 13px; resize: vertical; }
.shot { border: 1px dashed var(--line); border-radius: 6px; min-height: 56px; display: grid; place-items: center; padding: 8px; text-align: center; font-size: 12px; cursor: text; }
.shot:focus { outline: 2px solid var(--brand); }
.shot img { max-width: 100%; max-height: 160px; border-radius: 4px; }
.clear { font-size: 11px; align-self: flex-start; }
.thanks { padding: 24px; text-align: center; color: #2e7d32; }
.ft { display: flex; gap: 8px; justify-content: flex-end; padding: 10px 12px; border-top: 1px solid var(--line); }
</style>
